import { useState, useEffect, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import QRCode from 'react-qr-code';
import { authApi } from '../api';
import type { UserDto } from '../api';
import type { StoredTokens } from '../auth/tokenStorage';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { RateLimitBanner } from './RateLimitBanner';
import { useLoginFlow } from './useLoginFlow';

export function MfaEnrollmentPage() {
  const { state, dispatch } = useLoginFlow();
  const navigate = useNavigate();

  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [keyCopied, setKeyCopied] = useState(false);

  // Guard: must arrive here via the credentials flow.
  useEffect(() => {
    if (state.step !== 'mfa_enrollment') {
      void navigate('/login', { replace: true });
    }
  }, [state.step, navigate]);

  if (state.step !== 'mfa_enrollment') return null;

  const isRateLimited = (state.rateLimitUntil ?? 0) > Date.now();

  const copyKey = () => {
    void navigator.clipboard.writeText(state.manualEntryKey).then(() => {
      setKeyCopied(true);
      setTimeout(() => setKeyCopied(false), 2000);
    });
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const result = await authApi.enrollMfa({
        challengeToken: state.challengeToken,
        totpCode: code,
      });

      const { accessToken, refreshToken, expiresAt, user, recoveryCodes } = result;
      if (!accessToken || !refreshToken || !expiresAt) throw new Error('Incomplete response');

      const pendingTokens: StoredTokens = { accessToken, refreshToken, expiresAt };
      const pendingUser: UserDto | null = user ?? null;

      dispatch({
        type: 'RECOVERY_CODES',
        recoveryCodes: recoveryCodes ?? [],
        pendingTokens,
        pendingUser,
      });
      void navigate('/login/recovery-codes');
    } catch (err: unknown) {
      const res = (err as { response?: { status?: number; headers?: Record<string, string> } }).response;
      if (res?.status === 429) {
        const retryAfter = parseInt(res.headers?.['retry-after'] ?? '60', 10);
        dispatch({ type: 'RATE_LIMITED', retryAfterSeconds: retryAfter });
      } else if (res?.status === 401) {
        setError('Invalid code. Try again.');
      } else {
        setError('An error occurred. Try again.');
      }
    } finally {
      setLoading(false);
      setCode('');
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
      {/* ── Step 1: QR code ─────────────────────────────────────────────── */}
      <div>
        <p
          style={{
            margin: '0 0 12px',
            fontSize: 'var(--text-xs)',
            fontWeight: 600,
            color: 'var(--text-tertiary)',
            letterSpacing: '0.06em',
            textTransform: 'uppercase',
          }}
        >
          Step 1 — Scan with your authenticator app
        </p>
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            padding: 20,
            background: '#fff',
            border: '1px solid var(--border-default)',
            borderRadius: 'var(--radius-lg)',
          }}
        >
          <QRCode value={state.qrCodeUri} size={180} />
        </div>
      </div>

      {/* ── Manual key ──────────────────────────────────────────────────── */}
      <div>
        <p
          style={{
            margin: '0 0 8px',
            fontSize: 'var(--text-sm)',
            color: 'var(--text-secondary)',
          }}
        >
          Or enter the key manually:
        </p>
        <div style={{ display: 'flex', gap: 8 }}>
          <code
            style={{
              flex: 1,
              padding: '8px 12px',
              background: 'var(--warm-gray-50)',
              border: '1px solid var(--border-default)',
              borderRadius: 'var(--radius-md)',
              fontSize: 'var(--text-sm)',
              fontFamily: 'monospace',
              color: 'var(--text-primary)',
              wordBreak: 'break-all',
              lineHeight: 'var(--leading-normal)',
            }}
          >
            {state.manualEntryKey}
          </code>
          <Button variant="secondary" size="sm" onClick={copyKey} style={{ flexShrink: 0 }}>
            {keyCopied ? 'Copied!' : 'Copy'}
          </Button>
        </div>
      </div>

      {isRateLimited && state.rateLimitUntil && (
        <RateLimitBanner untilMs={state.rateLimitUntil} />
      )}

      {/* ── Step 2: Confirm code ─────────────────────────────────────────── */}
      <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <div>
          <p
            style={{
              margin: '0 0 8px',
              fontSize: 'var(--text-xs)',
              fontWeight: 600,
              color: 'var(--text-tertiary)',
              letterSpacing: '0.06em',
              textTransform: 'uppercase',
            }}
          >
            Step 2 — Enter the 6-digit code to confirm
          </p>
          <Input
            type="text"
            value={code}
            onChange={(e) => setCode(e.target.value.trim())}
            maxLength={6}
            inputMode="numeric"
            pattern="\d{6}"
            autoComplete="one-time-code"
            placeholder="000000"
            required
            disabled={isRateLimited}
            style={{ fontFamily: 'monospace', letterSpacing: '0.25em', textAlign: 'center' }}
          />
        </div>

        {error && (
          <div
            style={{
              background: 'var(--badge-rejected-bg)',
              border: '1px solid var(--badge-rejected-text)',
              borderRadius: 'var(--radius-md)',
              padding: '10px 14px',
              fontSize: 'var(--text-sm)',
              color: 'var(--badge-rejected-text)',
            }}
          >
            {error}
          </div>
        )}

        <Button
          type="submit"
          variant="primary"
          size="lg"
          loading={loading}
          disabled={isRateLimited}
          style={{ width: '100%' }}
        >
          {loading ? 'Confirming…' : 'Confirm and enable 2FA'}
        </Button>
      </form>
    </div>
  );
}
